import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/WebsocketContext';
import {
  DELETE_CONVERSATION,
  GET_CONVERSATION,
  GET_MY_CONVERSATIONS,
  MARK_CONVERSATION_READ,
  SEND_DIRECT_MESSAGE,
  SEND_TYPING_INDICATOR,
} from '../graphql/resolvers/directMessages/directMessages.gql';

export interface DmParticipant {
  id: string;
  username?: string | null;
  avatar?: string | null;
  // TRIL-396: true when the counterparty has ≥1 authenticated WS connection.
  online?: boolean | null;
}
export interface ConversationSummary {
  convId: string;
  otherUserId: string;
  otherUser?: DmParticipant | null;
  lastMessageAt?: string | null;
  preview?: string | null;
  unreadCount?: number | null;
  saved?: boolean | null;
}
export interface DirectMessage {
  convId: string;
  messageId: string;
  senderId: string;
  body: string;
  createdAt: string;
}

/**
 * TRIL-394: the caller's inbox. Refetches on any inbound `direct_message` WS event
 * so unread counts + ordering stay live.
 */
export function useConversations() {
  const { user } = useAuth();
  const isSignedIn = Boolean(user?.id);
  const { onEvent } = useSocket();

  const { data, loading, error, refetch } = useQuery(GET_MY_CONVERSATIONS, {
    fetchPolicy: 'cache-and-network',
    skip: !isSignedIn,
  });

  useEffect(() => {
    if (!isSignedIn) return;
    // Any new message anywhere can change the inbox (order + unread).
    const off = onEvent('direct_message', () => {
      void refetch();
    });
    return off;
  }, [isSignedIn, onEvent, refetch]);

  const conversations: ConversationSummary[] =
    data?.myConversations?.conversations ?? [];

  return { conversations, loading, error, refetch };
}

/**
 * TRIL-394: a single conversation thread + send/read/delete actions, with live
 * append when a `direct_message` for THIS conversation arrives. Messages are
 * returned oldest-first (chat order); the server returns newest-first.
 */
export function useConversation(convId: string | undefined) {
  const { onEvent } = useSocket();

  const { data, loading, error, refetch } = useQuery(GET_CONVERSATION, {
    variables: { convId },
    fetchPolicy: 'cache-and-network',
    skip: !convId,
  });

  const [sendMutation, { loading: sending }] = useMutation(SEND_DIRECT_MESSAGE);
  const [markReadMutation] = useMutation(MARK_CONVERSATION_READ);
  const [deleteMutation] = useMutation(DELETE_CONVERSATION);
  const [typingMutation] = useMutation(SEND_TYPING_INDICATOR);
  const [otherTyping, setOtherTyping] = useState(false);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const lastTypingSentRef = useRef(0);

  // Receive: show "typing…" for this conversation; auto-clear after 4s of silence.
  useEffect(() => {
    if (!convId) return;
    const off = onEvent('dm_typing', (payload: { convId?: string }) => {
      if (payload?.convId !== convId) return;
      setOtherTyping(true);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => setOtherTyping(false), 4000);
    });
    return () => {
      off();
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, [convId, onEvent]);

  // Live append: refetch when a message for this conversation lands.
  useEffect(() => {
    if (!convId) return;
    const off = onEvent('direct_message', (payload: { convId?: string }) => {
      if (payload?.convId === convId) void refetch();
    });
    return off;
  }, [convId, onEvent, refetch]);

  const send = useCallback(
    async (recipientId: string, body: string) => {
      const trimmed = body.trim();
      if (!trimmed) return;
      await sendMutation({ variables: { recipientId, body: trimmed } });
      await refetch();
    },
    [sendMutation, refetch],
  );

  const markRead = useCallback(async () => {
    if (!convId) return;
    // TRIL-505: refetch the inbox so its unread badge/aggregate clears — markConversationRead
    // zeroes the server count, but the myConversations observer won't reflect it otherwise.
    await markReadMutation({
      variables: { convId },
      refetchQueries: ['myConversations'],
    });
  }, [convId, markReadMutation]);

  // Send: debounced to at most once every 2.5s while the user types.
  const notifyTyping = useCallback(
    (recipientId: string) => {
      const now = Date.now();
      if (!recipientId || now - lastTypingSentRef.current < 2500) return;
      lastTypingSentRef.current = now;
      void typingMutation({ variables: { recipientId } });
    },
    [typingMutation],
  );

  const remove = useCallback(async () => {
    if (!convId) return;
    await deleteMutation({ variables: { convId } });
  }, [convId, deleteMutation]);

  const newestFirst: DirectMessage[] = data?.conversation?.messages ?? [];
  const messages = [...newestFirst].reverse(); // oldest-first for chat rendering

  return {
    messages,
    loading,
    error,
    sending,
    otherTyping,
    send,
    notifyTyping,
    markRead,
    remove,
    refetch,
  };
}
